/**
 * Dense-grid cell primitives.
 *
 * A risk grid is one row per market with the runner positions in fixed,
 * cross-event-aligned columns. Two things in that grid are easy to get wrong
 * and expensive to get wrong, so they live here rather than in a feature:
 *
 *  1. "This market has no such runner" and "the book is flat on this runner"
 *     are DIFFERENT FACTS and must not look alike. The product being replaced
 *     printed a dash for both, so a two-runner market and a market you were
 *     square on were indistinguishable at a glance.
 *  2. Sign must never be carried by colour alone. Every cell takes a screen-
 *     reader sentence, and the row's worst outcome takes a border marker as
 *     well as a tint.
 *
 * Money arrives pre-formatted, as everywhere else in the kit.
 */

import type { ReactNode, TdHTMLAttributes } from 'react'
import { cx, type Tone } from './core'

export interface ExposureCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'children'> {
  /**
   * Column slot. Drives the width and the hide-below step, so the same slot is
   * the same width in every event group down the page — which is the entire
   * argument for a grid over a stack of cards.
   */
  col: string
  /** Pre-formatted figure. Ignored when `absent`. */
  value?: ReactNode
  tone?: Tone
  /**
   * The row's worst outcome. Renders an inset rule as well as the tint, because
   * a desk that cannot see red still has to be able to find the number that
   * gates its credit.
   */
  worst?: boolean
  /**
   * This market has no such runner — the X of a two-runner market. Renders the
   * em dash in the absent treatment, which is deliberately quieter and
   * un-tinted so it cannot be mistaken for a zero position.
   */
  absent?: boolean
  /** sr-only sentence for the absent case. */
  absentLabel?: string
  /** sr-only sentence naming the runner and the direction, e.g. "Chelsea — the book pays ₹36,759". */
  srText?: ReactNode
  /** Heavier weight — event roll-up rows. */
  strong?: boolean
  hideBelow?: number
  align?: 'left' | 'right' | 'center'
}

export function ExposureCell({
  col,
  value,
  tone,
  worst,
  absent,
  absentLabel = 'no position in this market',
  srText,
  strong,
  hideBelow,
  align = 'right',
  className,
  ...rest
}: ExposureCellProps) {
  return (
    <td
      className={cx('w7-expo-cell', className)}
      data-col={col}
      data-tone={absent ? undefined : tone}
      data-worst={worst ? 'true' : undefined}
      data-absent={absent ? 'true' : undefined}
      data-strong={strong ? 'true' : undefined}
      data-hide-below={hideBelow}
      style={{ textAlign: align }}
      {...rest}
    >
      {absent ? (
        <>
          <span className="w7-expo-empty" aria-hidden="true">
            —
          </span>
          <span className="w7-sr">{absentLabel}</span>
        </>
      ) : (
        <>
          <span className="w7-mono">{value}</span>
          {srText ? <span className="w7-sr">{srText}</span> : null}
        </>
      )}
    </td>
  )
}

export interface ExposurePillProps {
  /** Runner or line name. */
  label: ReactNode
  /** Pre-formatted figure. */
  value: ReactNode
  tone?: Tone
  title?: string
  className?: string
}

/**
 * One runner of a field, or one fancy line, as a wrapped pill.
 *
 * A horse's book never appears under a column headed "1", because "1" means the
 * home side and a seven-runner handicap does not have one. Anything with four
 * or more runners gets this strip instead of a numbered slot, and a fancy-heavy
 * fixture puts eight numbers in two rows instead of the eight full-width rows
 * the original spent on them.
 *
 * A span, so the caller can wrap it in their router's link, or put
 * `className="w7-expo-pill"` straight onto that link — one selector, one owner,
 * either way.
 */
export function ExposurePill({ label, value, tone, title, className }: ExposurePillProps) {
  return (
    <span className={cx('w7-expo-pill', className)} data-tone={tone} title={title}>
      <span className="w7-expo-pill-name w7-truncate">{label}</span>
      <span className="w7-expo-pill-value w7-mono">{value}</span>
    </span>
  )
}
